import { useEffect } from 'react';
import { router } from '@/routes';
import { getAccessToken } from '@/services/api';

const SESSION_KEY = 'pce-pageview-session';
const VISITOR_COOKIE = 'pce_vid';
const VISITOR_MAX_AGE = 63072000; // 2 years in seconds

// Track previous SPA route for referrer (module-level so it persists across navigations)
let previousUrl: string | null = null;

function getSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

function getVisitorId(): string {
  // Read from cookie
  const match = document.cookie.match(new RegExp(`(?:^|; )${VISITOR_COOKIE}=([^;]*)`));
  if (match) return match[1];

  // Create new visitor ID and persist as cookie
  const visitorId = crypto.randomUUID();
  document.cookie = `${VISITOR_COOKIE}=${visitorId}; path=/; max-age=${VISITOR_MAX_AGE}; SameSite=Lax; Secure`;
  return visitorId;
}

/**
 * Logs frontend page views to the backend for first-party analytics.
 *
 * Tracking identifiers:
 * - visitorId: persistent cookie (2-year expiry) — tracks returning visitors across sessions
 * - sessionId: sessionStorage — groups page views within a single browser tab/session
 * - referrer: previous SPA route (not document.referrer which only reflects the initial page load)
 *
 * On the very first page load, referrer is set to document.referrer (the external
 * source, e.g. Google or a shared link). After that, it tracks SPA navigations.
 */
export function usePageViewLogger() {
  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/api';
    const sessionId = getSessionId();
    const visitorId = getVisitorId();

    const unsubscribe = router.subscribe((state) => {
      if (state.navigation.state !== 'idle') return;

      try {
        const currentUrl = state.location.pathname + state.location.search;

        // First page load uses the real document.referrer (external source).
        // Subsequent navigations use the previous SPA route.
        const referrer = previousUrl ?? (document.referrer || null);

        const payload = JSON.stringify({
          url: currentUrl,
          referrer,
          sessionId,
          visitorId,
        });

        // Update previous URL for next navigation
        previousUrl = currentUrl;

        const url = `${apiBase}/pageviews`;
        const token = getAccessToken();

        // Use fetch with keepalive when we have an auth token so the backend
        // can resolve the authenticated user. Fall back to sendBeacon for
        // unauthenticated users (sendBeacon can't send custom headers).
        if (token) {
          fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        } else if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(url, blob);
        } else {
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
          }).catch(() => {});
        }
      } catch {
        // Never throw — page view logging is non-critical
      }
    });

    return unsubscribe;
  }, []);
}
